/**
 * Helpers para construir series temporales de 8 semanas.
 *
 * Bucketeamos por semana ISO (lunes 00:00 UTC) hacia atrás desde "ahora".
 * El bucket [0] es la semana más antigua y [N-1] la actual — apto para
 * pasar directo al componente Sparkline.
 */

/** Cantidad de semanas que cubrimos en los sparklines del dashboard. */
export const WEEKS_WINDOW = 8;
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

/**
 * Devuelve el timestamp ms del lunes 00:00 UTC de la semana de `d`.
 * Usamos UTC para que los sparklines no se desordenen según timezone del cliente.
 */
function weekStartUtc(d: Date): number {
  const u = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  // getUTCDay: 0 = domingo, 1 = lunes...
  const dayOfWeek = u.getUTCDay();
  const offsetToMonday = (dayOfWeek + 6) % 7; // domingo→6, lunes→0, martes→1...
  u.setUTCDate(u.getUTCDate() - offsetToMonday);
  return u.getTime();
}

/** Inicio (ms) de la semana actual y del rango de N semanas atrás. */
export function getWindow(weeks = WEEKS_WINDOW): { startMs: number; endMs: number } {
  const endMs = weekStartUtc(new Date());
  const startMs = endMs - (weeks - 1) * MS_PER_WEEK;
  return { startMs, endMs };
}

/**
 * Agrupa registros por semana y aplica un reductor.
 *
 * @param rows  filas con un timestamp en ISO o ms
 * @param getTs   extrae el timestamp del row (devuelve null para descartar)
 * @param reducer  recibe los rows de cada semana, devuelve el valor del bucket
 */
export function bucketByWeek<T>(
  rows: T[],
  getTs: (r: T) => string | number | null | undefined,
  reducer: (rowsInWeek: T[]) => number,
  weeks = WEEKS_WINDOW,
): number[] {
  const { startMs } = getWindow(weeks);
  const buckets: T[][] = Array.from({ length: weeks }, () => []);

  for (const r of rows) {
    const raw = getTs(r);
    if (raw == null) continue;
    const ms = typeof raw === 'number' ? raw : new Date(raw).getTime();
    if (Number.isNaN(ms)) continue;
    const idx = Math.floor((ms - startMs) / MS_PER_WEEK);
    if (idx < 0 || idx >= weeks) continue;
    buckets[idx].push(r);
  }

  return buckets.map(reducer);
}

/**
 * Suma acumulada de los buckets — útil para series tipo
 * "alumnos totales activos al cierre de cada semana".
 *
 * Recibe los **incrementos por semana** y devuelve el running total +
 * el valor base (alumnos que ya existían antes del periodo).
 */
export function accumulate(deltas: number[], baseline = 0): number[] {
  let acc = baseline;
  return deltas.map((d) => (acc += d));
}

/**
 * Calcula el delta % entre el último bucket y el promedio de los anteriores.
 * Devuelve null si la base es 0/inválida (evita divisiones por cero).
 *
 * Convención: positivo = mejoró, negativo = empeoró.
 */
export function trendDelta(values: number[]): number | null {
  if (values.length < 2) return null;
  const last = values[values.length - 1];
  const prev = values.slice(0, -1);
  const validPrev = prev.filter((v) => Number.isFinite(v));
  if (validPrev.length === 0) return null;
  const avgPrev = validPrev.reduce((a, b) => a + b, 0) / validPrev.length;
  if (avgPrev === 0) return null;
  return ((last - avgPrev) / avgPrev) * 100;
}
